import InboxDomain from "entities/domain/inbox";
import {
  CloseAgentPolicy,
  CloseInboxPolicy,
  TemplateScope,
} from "entities/dto/ManagedInboxDTO";

export const HAS_CONVERSATIONS_ARCHIVE_BLOCKER = "has_conversations";
export const SOURCE_DESTINATION_ARCHIVE_BLOCKER = "source_destination";

// What sends new enquiries to the Inbox (fuzey-api source routing). Each
// lead source names its own kinds; an unknown kind is still shown, generically.
// An Auto Trader connection whose destination is the Inbox.
export const CONFIGURED_SOURCE_REFERENCE = "auto_trader_dealership";
// An Auto Trader lead whose intake is under way and will route to the Inbox.
export const PENDING_LEAD_SOURCE_REFERENCE = "pending_auto_trader_lead";
// A lead email address whose destination is the Inbox.
export const LEAD_EMAIL_HANDLER_SOURCE_REFERENCE = "lead_email_handler";
// A lead from a lead email address, still being set up, that will route to
// the Inbox.
export const PENDING_EMAIL_LEAD_SOURCE_REFERENCE = "pending_email_lead";

export interface InboxSourceReference {
  kind: string;
  // Auto Trader kinds: the dealer ID.
  dealershipId: string | null;
  dealershipName: string | null;
  // Lead email kinds: the address and its source.
  emailHandlerId: string | null;
  email: string | null;
  source: string | null;
  // Pending kinds.
  leadId: string | null;
  dealId: string | null;
  decidedAt: string | null;
}

// The server's coverage states (fuzey-api src/api/inbox/coverage.py).
export const NO_ELIGIBLE_MEMBER_COVERAGE = "no_eligible_member";
export const NO_INTERRUPTING_ROUTE_COVERAGE = "no_interrupting_route";
export const COVERED_COVERAGE = "covered";

export type InboxCoverageState =
  | typeof NO_ELIGIBLE_MEMBER_COVERAGE
  | typeof NO_INTERRUPTING_ROUTE_COVERAGE
  | typeof COVERED_COVERAGE;

/**
 * Whether the Inbox's members are set up to be alerted about its unassigned
 * work: configuration, not delivery. Always the server's calculation.
 */
export interface InboxCoverage {
  state: InboxCoverageState;
  eligibleMemberCount: number;
  reachableMemberCount: number;
}

export interface InboxArchiveBlocker {
  code: string;
  message: string;
  openConversationCount: number | null;
  closedConversationCount: number | null;
  sourceReferences: InboxSourceReference[];
}

/**
 * An Inbox as Settings → Inboxes manages it (entities/dto/ManagedInboxDTO).
 * Navigation keeps reading plain InboxDomain from the active list; only the
 * settings screen reads this.
 */
export default class ManagedInboxDomain extends InboxDomain {
  // What belongs in this Inbox; null when it has none.
  purpose: string | null;

  version: number;

  archivedAt: string | null;

  canRename: boolean;

  canEditPurpose: boolean;

  canArchive: boolean;

  // The server's reasons, so the client never counts conversations itself.
  archiveBlockers: InboxArchiveBlocker[];

  // What a staff close in this Inbox does; automatic closes ignore both.
  closeAgentPolicy: CloseAgentPolicy;

  closeInboxPolicy: CloseInboxPolicy;

  canEditCloseAgentPolicy: boolean;

  // False for General Enquiries: it is where a close moves conversations.
  canEditCloseInboxPolicy: boolean;

  // Null when the server has no members API (before fuzey-api PR 3288); false
  // for an archived Inbox.
  canEditMembers: boolean | null;

  // Null for an archived Inbox, an unknown state, or no members API.
  coverage: InboxCoverage | null;

  constructor(
    id: string,
    key: string,
    name: string,
    isActive: boolean,
    purpose: string | null,
    version: number,
    archivedAt: string | null,
    canRename: boolean,
    canEditPurpose: boolean,
    canArchive: boolean,
    archiveBlockers: InboxArchiveBlocker[],
    closeAgentPolicy: CloseAgentPolicy,
    closeInboxPolicy: CloseInboxPolicy,
    canEditCloseAgentPolicy: boolean,
    canEditCloseInboxPolicy: boolean,
    canEditMembers: boolean | null,
    coverage: InboxCoverage | null,
    public templateScope: TemplateScope | null = null,
    public canEditTemplates: boolean | null = null
  ) {
    super(id, key, name, isActive);
    this.purpose = purpose;
    this.version = version;
    this.archivedAt = archivedAt;
    this.canRename = canRename;
    this.canEditPurpose = canEditPurpose;
    this.canArchive = canArchive;
    this.archiveBlockers = archiveBlockers;
    this.closeAgentPolicy = closeAgentPolicy;
    this.closeInboxPolicy = closeInboxPolicy;
    this.canEditCloseAgentPolicy = canEditCloseAgentPolicy;
    this.canEditCloseInboxPolicy = canEditCloseInboxPolicy;
    this.canEditMembers = canEditMembers;
    this.coverage = coverage;
  }

  isArchived(): boolean {
    return !this.isActive;
  }

  // General Enquiries keeps its name but can still have a purpose and an
  // adviser close setting.
  isEditable(): boolean {
    return (
      this.isActive &&
      (this.canRename ||
        this.canEditPurpose ||
        this.canEditCloseAgentPolicy ||
        this.canEditCloseInboxPolicy ||
        this.canEditMembers === true ||
        this.canEditTemplates === true)
    );
  }

  // Whether the server serves the members API: archived Inboxes included,
  // read-only.
  areMembersAvailable(): boolean {
    return this.canEditMembers !== null;
  }

  areTemplatesAvailable(): boolean {
    return this.templateScope !== null && this.canEditTemplates !== null;
  }

  // A protected destination is never archived from settings, whatever it
  // contains. Identified by its key and server metadata, never by its name.
  isProtected(): boolean {
    return this.isGeneralEnquiries();
  }
}
